import { defineStore } from "pinia";
import api from "@/modules/api";
import type { JSONContent } from "@tiptap/core";
import { isImage, isValidJSON } from "@/modules/common";
import { StarterKit } from "@tiptap/starter-kit";
import EditorImage from "@/components/mobile/editor-ext/editor-image";
import { useMate } from "@/stores/mate";
import editorUtils from "@/modules/editor/editor-utils";
import { useMoment } from "@/stores/moment";
import { CharacterCount } from "@tiptap/extension-character-count";
import ParagraphIndent from "@/components/mobile/editor-ext/paragraph-indent";
import { useReply } from "@/stores/reply";
import { useStoryBook } from "@/stores/story-book";
import i18n from "@/locales";
import {
  Media,
  Moment,
  Story,
  StoryBook,
  StoryChapter,
  Visibility,
} from "@/types/entity";

const PAGE_SIZE = 999;

interface StoryState {
  storyWithoutBook: {
    list: string[];
    init: boolean;
  };
  story: {
    list: string[]; // uuid[]
    changed: boolean;
  };
  storyEditList: number[];
  storyBookId2StoryUuids: Record<number, string[]>;
  storyMap: Record<string, Story>;
  img: {
    selectTab: number;
    activeIdx: number;
    showCustomModal: boolean;
    contentList: any[];
    custom: {
      prompt: string;
      traitId?: number;
      style?: number;
    };
    promptLoading: boolean;
    cache: {
      traitId?: number;
      style?: number;
    };
  };
}

export const useRenderExtension = () => [
  StarterKit,
  CharacterCount,
  EditorImage,
  ParagraphIndent,
];
export const useStory = defineStore("story", {
  state: (): StoryState => {
    return {
      storyWithoutBook: {
        list: [],
        init: false,
      },
      story: {
        list: [],
        changed: false,
      },
      storyEditList: [],
      storyBookId2StoryUuids: {},
      storyMap: {},
      img: {
        selectTab: 0,
        activeIdx: 0,
        contentList: [],
        showCustomModal: false,
        promptLoading: false,
        custom: {
          prompt: "",
          traitId: undefined,
          style: undefined,
        },
        cache: {
          traitId: undefined,
          style: undefined,
        },
      },
    };
  },
  getters: {
    storyList: (state): Story[] => {
      return state.story.list.map((id) => state.storyMap[id]);
    },
    storyWithoutBookList: (state): Story[] => {
      return state.storyWithoutBook.list.map((id) => state.storyMap[id]);
    },
    getStoryEditList: (state): Story[] => {
      return state.storyEditList.map((id) => state.storyMap[id]);
    },
  },
  actions: {
    async getStoryList(): Promise<Story[]> {
      if (
        this.story.list &&
        !this.story.changed &&
        this.story.list.length > 0
      ) {
        return this.storyList;
      } else {
        const mate = useMate();
        const storyList = await this.fetchStoryList({
          mateId: mate.currentMate!.id,
        });
        this.story.list = storyList.map((item) => item.uuid);
        this.story.changed = false;
        return storyList;
      }
    },
    async getEditStoryList(storyBookId?: number): Promise<Story[]> {
      const mate = useMate();
      const storyList = await this.fetchStoryList({
        mateId: mate.currentMate!.id,
        storyBookId,
      });
      this.storyEditList = storyList.map((item) => {
        this.storyMap[item.id!] = item;
        return item.id!;
      });
      return storyList;
    },
    async getStoryListByStoryBook(
      storyBook: StoryBook,
      publicOnly = false,
      mateId?: number,
      force?: boolean
    ): Promise<Story[]> {
      const storyBookId = storyBook.id!;
      if (
        this.storyBookId2StoryUuids[storyBookId] &&
        this.storyBookId2StoryUuids[storyBookId].length > 0 &&
        !force
      ) {
        const uuids = this.storyBookId2StoryUuids[storyBookId];
        return uuids.map((uuid) => this.storyMap[uuid]);
      } else {
        const mate = useMate();
        const storyList = await this.fetchStoryList({
          storyBookId,
          mateId: mateId ?? mate.currentMate!.id,
          published: publicOnly ? 1 : undefined,
        });
        this.storyBookId2StoryUuids[storyBookId] = storyList.map(
          (item) => item.uuid
        );
        return storyList;
      }
    },
    async fetchStoryList(
      filter: {
        mateId?: number;
        published?: 0 | 1;
        storyBookId?: number;
        storyId?: number;
      } = {}
    ): Promise<Story[]> {
      const res = await api.readStoryPage({
        filter,
        pagination: { page: 1, perPage: PAGE_SIZE },
        sort: { field: "order", order: "asc" },
      });
      this.updateStoryMap(res.page.data);
      return res.page.data;
    },
    disableCache(clearData = false): void {
      this.story.changed = true;
      if (clearData) {
        this.story.list = [];
        this.storyBookId2StoryUuids = {};
        this.storyWithoutBook.list = [];
        this.storyWithoutBook.init = false;
      }
    },
    /** 不传storyBookId会自动创建storyBook */
    async createStoryByMoments(momentIds: number[], storyBookId?: number) {
      const res = await api.storyGenerate({
        storyBookId,
        momentIdzz: momentIds,
      });
      const storyBookStore = useStoryBook();
      if (!storyBookId) {
        // 新建storyBook和story => 刷新故事列表
        await storyBookStore.getStoryBookList(true);
      }
      const newStory = await this.getStoryItem(res.item.uuid, true);
      this.updateStoryMap([newStory]);
      this.unshiftStory(res.item);
    },
    async createStory(params: {
      storyBookId: number;
      mediaId: number;
      title: string;
      description: string;
      mateId: number;
    }) {
      const res = await api.createOneStory(params);
      const newStory = await this.getStoryItem(res.item.uuid, true);
      this.updateStoryMap([newStory]);
      this.unshiftStory(res.item);
      return res.item;
    },
    unshiftStory(story: Story) {
      this.story.list.unshift(story.uuid);
      if (story.storyBookId) {
        if (!this.storyBookId2StoryUuids[story.storyBookId]) {
          this.storyBookId2StoryUuids[story.storyBookId] = [];
        }
        this.storyBookId2StoryUuids[story.storyBookId].unshift(story.uuid);
      }
    },
    async getStoryItem(uuid: string, withMoment = false): Promise<Story> {
      const storyItem = this.storyMap[uuid];
      const needButWithoutMoment =
        withMoment &&
        (!storyItem ||
          !storyItem.storyChapterzz ||
          storyItem.storyChapterzz.length === 0 ||
          !storyItem.storyChapterzz.some((item) => item.moment));
      let targetStory = storyItem;
      if (!storyItem || needButWithoutMoment) {
        targetStory = await this.fetchStoryItem(uuid);
      }
      const isJson = isValidJSON(targetStory.text);
      if (!isJson) {
        const { jsonContent, imgList } = textToJsonAndSupplyImage(targetStory);
        const text = JSON.stringify(jsonContent);
        const mateStore = useMate();
        if (mateStore.isMyMate(targetStory.mate.uuid)) {
          await this.updateStory(targetStory.id!, {
            text,
            // 未设置mediaId时，使用第一张图片
            mediaId:
              !targetStory.mediaId && imgList.length > 0
                ? imgList[0]?.mediaId
                : undefined,
          });
        } else {
          // HACK：从未在编辑器打开过AI生成的故事，直接被发布，会以text形式存储
          // 若被其他用户阅读，会触发此处，仅在内存中更新text
          targetStory.text = text;
        }
        return this.storyMap[uuid];
      }
      return targetStory;
    },
    async fetchStoryItem(uuid: string): Promise<Story> {
      const res = await api.readOneStory(uuid);
      this.updateStoryMap([res.item]);
      return this.storyMap[res.item.uuid];
    },
    async deleteStory(story: Story) {
      await api.deleteOneStory(story.id!);
      this.story.list = this.story.list.filter((uuid) => uuid !== story.uuid);
      if (story.storyBookId && this.storyBookId2StoryUuids[story.storyBookId]) {
        this.storyBookId2StoryUuids[story.storyBookId] =
          this.storyBookId2StoryUuids[story.storyBookId].filter(
            (uuid) => uuid !== story.uuid
          );
      }
    },
    async updateStory(
      id: number,
      params: Partial<
        Pick<
          Story,
          | "title"
          | "description"
          | "mediaId"
          | "dtPublish"
          | "text"
          | "videoMediaId"
          | "visibility"
        >
      >
    ) {
      const res = await api.updateOneStory(id, params);
      this.updateStoryMap([res.item]);
      return this.storyMap[res.item.uuid];
    },
    getJson(text: string): JSONContent {
      if (isValidJSON(text)) {
        return JSON.parse(text) as JSONContent;
      } else {
        return editorUtils.getJsonFromPlainText(text);
      }
    },
    updateStoryMap(data: Story[]) {
      const replyStore = useReply();
      data.forEach((item) => {
        const old = this.storyMap[item.uuid] ?? {};
        this.storyMap[item.uuid] = Object.assign(old, item);
        if (item.reply) {
          const reply = replyStore.updateMap([item.reply]);
          this.storyMap[item.uuid].reply = reply[0];
        }
      });
    },
    removeStoryItemFromMap(uuid: string) {
      delete this.storyMap[uuid];
    },
    async duplicateStory(id: number) {
      const res = await api.storyDuplicate(id);
      const story = await this.getStoryItem(res.item.uuid, true);
      this.updateStoryMap([story]);
      this.unshiftStory(res.item);
    },
    async storyWriteByText(
      id: number,
      prevText: string,
      belowText: string = ""
    ): Promise<string> {
      const res = await api.storyWrite({
        storyId: id,
        storyText: `${prevText}"""${i18n.global.t(
          "Texts.completeContent"
        )}"""${belowText}`,
      });
      return res.item.text;
    },
    async storyWriteByMoment(
      id: number,
      momentId: number,
      momentText: string
    ): Promise<string> {
      const res = await api.storyWrite({
        storyId: id,
        momentId,
        momentText,
      });
      return res.item.text;
    },
    async publishStory(
      storyId: number,
      storyBookUuid: string,
      text: string,
      visibility: Visibility
    ) {
      // 1. 更新发布状态
      const dtPublish = new Date().toISOString();
      await this.updateStory(storyId, { dtPublish, visibility });
      // 2. 判断storyBook的发布状态，storyBook未发布则进行发布
      const storyBookStore = useStoryBook();
      const sb = await storyBookStore.getOrFetchStoryItem(storyBookUuid, true);
      if (!sb.dtPublish) {
        await storyBookStore.updateStoryBook(sb.id, { dtPublish });
      }
      // 3. 发Moment
      const mate = useMate();
      await api.createMoment({
        mateId: mate.currentMate!.uuid,
        storyId,
        text,
        visibility,
        status: 1,
      });
      const moment = useMoment();
      await moment.initMomentListIfEmpty(true, 0);
    },
    async storyGenerated(storyUuid: string) {
      await this.fetchStoryItem(storyUuid);
      const storyBookStore = useStoryBook();
      await storyBookStore.getStoryBookList(true);
    },
    // async storyWriteByMoment(id: number, momentId: number, momentText: string) {
    //   const res = await api.storyWrite({
    //     storyId: id,
    //     momentId,
    //     momentText,
    //   });
    //   return res.item.map((item) => item.text);
    // },
    async storyScreenshot(uuid: string) {
      const res = await api.storyScreenshot(uuid);
      return res.item;
    },
    async getOrFetchStoryWithoutBookList(): Promise<Story[]> {
      if (this.storyWithoutBook.init) {
        return this.storyWithoutBook.list.map((uuid) => this.storyMap[uuid]);
      } else {
        const mate = useMate();
        const storyList = await this.fetchStoryList({
          mateId: mate.currentMate!.id,
          storyBookId: 0,
        });
        this.storyWithoutBook.list = storyList.map((item) => item.uuid);
        this.storyWithoutBook.init = true;
        return storyList;
      }
    },
    async changeStoryBookId(storyBookId: number, storyIds: number[]) {
      await api.storyBookImportStory(storyBookId, storyIds);
      Object.values(this.storyMap).forEach((story) => {
        if (storyIds.includes(story.id!)) {
          story.storyBookId = storyBookId;
          this.storyWithoutBook.list = this.storyWithoutBook.list.filter(
            (uuid) => uuid !== story.uuid
          );
        }
      });
      this.storyBookId2StoryUuids[storyBookId] = [];
    },
    initImgModal() {
      const { img } = this;
      const { contentList, activeIdx, cache } = img;
      img.selectTab = 0;
      img.showCustomModal = false;
      const { attrs } = contentList[activeIdx];
      if (!attrs || !attrs.alt || !(typeof attrs.alt === "string")) {
        img.custom = {
          ...{
            prompt: "",
            traitId: undefined,
            style: undefined,
          },
          ...cache,
        };
        return;
      }
      const [_, prompt, traitId, style] = attrs.alt.split("$$$$");
      if (prompt) img.custom.prompt = prompt;
      if (traitId) img.custom.traitId = Number(traitId);
      if (style) img.custom.style = Number(style);
    },
  },
});

/**
 * 从Chapter中获取去重后的MomentList
 * @param chapters
 */
export function getMomentsFromChapter(
  chapters: StoryChapter[] | null
): Moment[] {
  if (!chapters) {
    return [];
  }
  const momentSet = new Set<number>();
  return chapters
    .filter((chapter) => {
      if (!chapter.moment || momentSet.has(chapter.moment.id)) {
        return false;
      }
      momentSet.add(chapter.moment.id);
      return true;
    })
    .map((chapter) => chapter.moment!);
}

/**
 * 若text为JSON，则直接返回JSON
 * 若text为纯文本，则生成JSON，并将Moment中的图片插入到JSON中
 * @param story
 */
export function textToJsonAndSupplyImage(story: Story): {
  jsonContent: JSONContent;
  imgList: {
    url: string;
    mediaId: number;
  }[];
} {
  const text = story.text;
  if (isValidJSON(text)) {
    return { jsonContent: JSON.parse(text) as JSONContent, imgList: [] };
  }
  const json = editorUtils.getJsonFromPlainText(text);
  const moments = getMomentsFromChapter(story.storyChapterzz);
  const images = moments
    .filter((moment) => moment.mediazz && moment.mediazz.length > 0)
    .reduce(
      (acc, moment) => {
        const mediaIdAndUrlList = moment.mediazz
          .filter((media) => isImage(media.url))
          .map((media) => ({ url: media.url, mediaId: (media as Media).id }));
        acc.push(...mediaIdAndUrlList);
        return acc;
      },
      [] as {
        url: string;
        mediaId: number;
      }[]
    );
  if (images.length === 0) {
    return { jsonContent: json as JSONContent, imgList: [] };
  }
  const imageNodes = images.map(({ url }) => ({
    type: "editorImage",
    attrs: { src: url },
  }));
  const newContent = [] as JSONContent[];
  let paragraphIdx = 0;
  for (const item of json.content ?? []) {
    newContent.push(item);
    if (paragraphIdx >= imageNodes.length) {
      continue;
    }
    if (item.type === "paragraph") {
      newContent.push(imageNodes[paragraphIdx++]);
    }
  }
  json.content = newContent;
  return { jsonContent: json as JSONContent, imgList: images };
}
